import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useRestrictedWords } from '@/modules/restricted-words/hooks/use-restricted-words.ts'
import { restrictedWordsService } from '@/modules/restricted-words/service/restricted-words-service.ts'
import type { RestrictedWord } from '@/modules/restricted-words/types.ts'
import { AddRestrictedWordDialog } from '@/modules/restricted-words/ui/add-restricted-word-dialog.tsx'
import { CheckWordsDialog } from '@/modules/restricted-words/ui/check-words-dialog.tsx'
import { DeleteWordsConfirmationDialog } from '@/modules/restricted-words/ui/delete-words-confirmation-dialog.tsx'
import { EditRestrictedWordDialog } from '@/modules/restricted-words/ui/edit-restricted-word-dialog.tsx'
import { RestrictedWordsItem } from '@/modules/restricted-words/ui/restricted-words-item.tsx'
import { RestrictedWordsTools } from '@/modules/restricted-words/ui/restricted-words-tools.tsx'
import { handleError } from '@/utils/error'

const ItemSkeleton = () => (
  <Skeleton className="h-[64px] dark:bg-secondary-500" />
)

const itemsPerPage = 48

export const RestrictedWords = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [isAddOpen, setAddOpen] = useState(false)
  const [isCheckOpen, setCheckOpen] = useState(false)
  const [isSelectionOpen, setSelectionOpen] = useState(false)
  const [isToggleSelectedLoading, setToggleSelectedLoading] = useState(false)
  const [isDeleteSelectedLoading, setDeleteSelectedLoading] = useState(false)
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState<RestrictedWord[]>([])
  const [page, setPage] = useState(0)
  const [wordsState, setWordsState] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [editingItem, setEditingItem] = useState<RestrictedWord>()
  const { restrictedWords, isRestrictedWordsLoading } = useRestrictedWords()
  const selectedItemsFromList = useMemo(
    () =>
      restrictedWords?.filter((w) => selectedItems.some((i) => i.id === w.id)),
    [restrictedWords, selectedItems],
  )

  useEffect(() => {
    setPage(0)
  }, [wordsState, searchQuery])

  const filteredItems = useMemo(() => {
    return restrictedWords
      ?.filter((w) =>
        wordsState === 'all'
          ? true
          : wordsState === 'custom'
            ? !w.common
            : w.common,
      )
      .filter((w) => w.word.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [restrictedWords, wordsState, searchQuery])

  const itemsToShow = useMemo(
    () => filteredItems?.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
    [page, filteredItems],
  )
  const totalPages = Math.ceil((restrictedWords?.length ?? 0) / itemsPerPage)

  const handleAddClose = useCallback(() => {
    setAddOpen(false)
  }, [])

  const handleAddOpen = useCallback(() => {
    setAddOpen(true)
  }, [])

  const handleCheckOpen = useCallback(() => {
    setCheckOpen(true)
  }, [])

  const handleCheckClose = useCallback(() => {
    setCheckOpen(false)
  }, [])

  const handleEditClick = useCallback((item: RestrictedWord) => {
    setEditingItem(item)
  }, [])

  const handleEditClose = useCallback(() => {
    setEditingItem(undefined)
  }, [])

  const handleSelectToggle = useCallback(() => {
    setSelectionOpen((old) => {
      if (old) {
        setSelectedItems([])
      }

      return !old
    })
  }, [])

  const handleItemSelection = useCallback((item: RestrictedWord) => {
    setSelectedItems((old) => {
      if (old.some((i) => i.id === item.id)) {
        return old.filter((o) => o.id !== item.id)
      } else {
        return [...old, item]
      }
    })
  }, [])

  const isAllSelectedCommon = useMemo(
    () =>
      !!selectedItemsFromList?.length &&
      selectedItemsFromList.every((i) => i.common),
    [selectedItemsFromList],
  )
  const isAllSelectedCustom = useMemo(
    () =>
      !!selectedItemsFromList?.length &&
      selectedItemsFromList.every((i) => !i.common),
    [selectedItemsFromList],
  )

  const handleToggleSelected = useCallback(
    (state: boolean) => {
      const promises = selectedItemsFromList?.map((i) =>
        restrictedWordsService.overrideRestrictedWord(i.id, !state),
      )

      setToggleSelectedLoading(true)

      Promise.all(promises ?? [])
        .then(() => {
          toast.success(`${!state ? 'Disabled' : 'Enabled'} selected words`)
          queryClient.invalidateQueries(['restrictedWords'])
        })
        .catch((error) => {
          handleError(
            error,
            'Failed to change words state, please try again later',
          )
        })
        .finally(() => {
          setToggleSelectedLoading(false)
        })
    },
    [queryClient, selectedItemsFromList, toast],
  )

  const handleDeleteSelected = useCallback(() => {
    const promises = selectedItemsFromList?.map((i) =>
      restrictedWordsService.deleteRestrictedWord(i.id),
    )

    setDeleteSelectedLoading(true)

    Promise.all(promises ?? [])
      .then(() => {
        toast.success(`Deleted selected words`)
        queryClient.invalidateQueries(['restrictedWords'])
      })
      .catch((error) => {
        handleError(error, 'Failed to delete words, please try again later')
      })
      .finally(() => {
        setDeleteSelectedLoading(false)
      })
  }, [queryClient, selectedItemsFromList, toast])

  const handleDeleteSelectedClick = useCallback(
    () => setDeleteConfirmationOpen(true),
    [],
  )

  const handleDeleteDialogClose = useCallback(
    () => setDeleteConfirmationOpen(false),
    [],
  )

  const handleDeleteConfirm = useCallback(() => {
    handleDeleteDialogClose()
    handleDeleteSelected()
  }, [handleDeleteDialogClose, handleDeleteSelected])

  return (
    <>
      <div>
        <RestrictedWordsTools
          isSelectionOpen={isSelectionOpen}
          onSelectToggle={handleSelectToggle}
          selectedItems={selectedItemsFromList ?? []}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          wordsState={wordsState}
          setWordsState={setWordsState}
          onAddOpen={handleAddOpen}
          isAllSelectedCommon={isAllSelectedCommon}
          isAllSelectedCustom={isAllSelectedCustom}
          onToggleSelected={handleToggleSelected}
          onDeleteSelectedClick={handleDeleteSelectedClick}
          onCheckClick={handleCheckOpen}
          isToggleSelectedLoading={isToggleSelectedLoading}
          isDeleteSelectedLoading={isDeleteSelectedLoading}
        />
        {!isRestrictedWordsLoading && itemsToShow?.length === 0 ? (
          <EmptyTableContent className="w-full">
            No words found.
          </EmptyTableContent>
        ) : (
          <div className="grid grid-cols-2 gap-2 rounded-2xl bg-card p-6 md:grid-cols-4 lg:grid-cols-8">
            {isRestrictedWordsLoading
              ? Array.from({ length: 23 }).map((_, index) => (
                  <ItemSkeleton key={index} />
                ))
              : itemsToShow?.map((item) => (
                  <RestrictedWordsItem
                    key={item.id}
                    item={item}
                    onEditClick={handleEditClick}
                    isSelectionOpen={isSelectionOpen}
                    onSelect={handleItemSelection}
                    isSelected={selectedItems.some((i) => i.id === item.id)}
                  />
                ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-end">
          <p className="text-sm">
            {page + 1} of {totalPages}
          </p>
          <Button
            onClick={() => setPage((old) => old - 1)}
            disabled={page === 0}
            variant="outline"
            className="ml-2.5"
          >
            Previous
          </Button>
          <Button
            onClick={() => setPage((old) => old + 1)}
            className="ml-1.5"
            disabled={page + 1 === totalPages}
            variant="outline"
          >
            Next
          </Button>
        </div>
      </div>
      <AddRestrictedWordDialog
        open={isAddOpen}
        onClose={handleAddClose}
      />
      {editingItem && (
        <EditRestrictedWordDialog
          open={!!editingItem}
          onClose={handleEditClose}
          word={editingItem}
        />
      )}
      <CheckWordsDialog
        open={isCheckOpen}
        onClose={handleCheckClose}
      />
      <DeleteWordsConfirmationDialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteDialogClose}
        onDelete={handleDeleteConfirm}
        selectedCount={selectedItems.length}
      />
    </>
  )
}
